import React, { useState, useEffect } from 'react'
import { Box, Typography, TextField, InputAdornment, IconButton, Tooltip } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import MapHeaderIcon from './MapHeaderIcon'
import PdfIcon from '@assets/map_header_icons/Pdf.svg'
import ExcelIcon from '@assets/map_header_icons/Excel.svg'
import CopyIcon from '@assets/map_header_icons/Copy.svg'
import PrintIcon from '@assets/map_header_icons/Print.svg'
import SearchIcon from '@assets/map_header_icons/search.svg'
import ProSearch from './ProSearch'
import { exportToExcel, exportToPDF, copyToClipboard } from '@/utils/exportUtils'
import { useTableStore } from '@/store/table/table'
import ArrowBackIosNewIcon from '@assets/sensors/ArrowBack.svg'
import { useAppTheme } from '@/theme/ThemeContext'

type PageHeaderWithIconsProps = {
  title?: string
  showSearch?: boolean
  showSearchInput?: boolean
  padding?: boolean
  placeholder?: string
  proSearch?: boolean
  onSearchChange?: (v: string) => void
  moveSearchToIcon?: boolean
  executeSearch?: (value: string) => void
  onClearFilters?: () => void
  showBackArrow?: boolean
  backTo?: string
  onBackClick?: () => void
  onExcelExport?: () => void
  onPdfExport?: () => void
  onCopy?: () => void
}

const PageHeaderWithIcons: React.FC<PageHeaderWithIconsProps> = ({
  title,
  showSearch = false,
  showSearchInput = true,
  padding = true,
  placeholder,
  proSearch = false,
  onSearchChange,
  moveSearchToIcon = false,
  executeSearch,
  onClearFilters,
  showBackArrow = false,
  backTo,
  onBackClick,
  onExcelExport,
  onPdfExport,
  onCopy,
}) => {
  const navigate = useNavigate()
  const { exportRowsStore, exportColumnsStore, filteredRowsStore } =
    useTableStore()
  const [searchInputValue, setSearchInputValue] = useState(
    filteredRowsStore.searchFilter?.value || ''
  )

  useEffect(() => {
    const storeValue = filteredRowsStore.searchFilter?.value || ''
    if (storeValue !== searchInputValue) {
      setSearchInputValue(storeValue)
    }
  }, [filteredRowsStore.searchFilter?.value])
  
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  
  const colors = {
    text: isDarkMode ? '#BBD7FF' : '#003F61',
    inputBg: isDarkMode ? '#1D325A' : '#fff',
    inputBorder: isDarkMode ? '#374151' : '#323B771A',
    inputText: isDarkMode ? '#FFFFFF' : '#003F61',
    iconFilter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
  }

  const iconConfigs = [
    {
      icon: PrintIcon,
      alt: 'Print',
      tooltip: 'הדפסה',
      action: 'print',
      onClick: () => {
        window.print()
      },
    },
    {
      icon: CopyIcon,
      alt: 'Copy',
      tooltip: 'העתקה',
      action: 'copy',
      onClick: () => {
        if (onCopy) {
          onCopy()
        } else {
          copyToClipboard(exportColumnsStore, exportRowsStore)
        }
      },
    },
    {
      icon: ExcelIcon,
      alt: 'Excel',
      tooltip: 'ייצוא ל-Excel',
      action: 'excel',
      onClick: () => {
        if (onExcelExport) {
          onExcelExport()
        } else {
          exportToExcel(exportColumnsStore, exportRowsStore)
        }
      },
    },
    {
      icon: PdfIcon,
      alt: 'PDF',
      tooltip: 'ייצוא ל-PDF',
      action: 'pdf',
      onClick: () => {
        if (onPdfExport) {
          onPdfExport()
        } else {
          exportToPDF(exportColumnsStore, exportRowsStore)
        }
      },
    },
  ]
  const orderedIconConfigs = [
    iconConfigs[2],
    iconConfigs[3],
    iconConfigs[1],
    iconConfigs[0],
  ]

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        direction: 'rtl',
        paddingTop: padding ? 'clamp(24px, 3vw, 48px)' : 0,
        paddingBottom: padding ? 'clamp(24px, 3vw, 48px)' : 0,
        paddingRight: padding ? 'clamp(12px, 1.2vw, 24px)' : 0,
        paddingLeft: padding ? 'clamp(16px, 1.8vw, 32px)' : 0,
      }}
    >
      {/* 1. Title Section (Right in RTL flex-start) */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-start' }}>
        {title && (
          <Typography
            fontWeight={700}
            fontSize="clamp(32px, 2.5vw, 48px)"
            lineHeight={1.2}
            color={colors.text}
            fontFamily="Rubik, sans-serif"
          >
            {title}
          </Typography>
        )}
        {showBackArrow && (
          <Tooltip title="חזרה" arrow>
            <IconButton
              onClick={() => onBackClick ? onBackClick() : backTo ? navigate(backTo) : navigate(-1)}
              sx={{ width: 40, height: 40, borderRadius: '8px' }}
            >
              <img src={ArrowBackIosNewIcon} alt='back' style={{ filter: colors.iconFilter }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* 2. Search Section (Center) */}
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {showSearch && !moveSearchToIcon && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showSearchInput && (
              <TextField
                value={searchInputValue}
                size="small"
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') executeSearch?.(searchInputValue)
                }}
                onChange={e => {
                  onSearchChange?.(e.target.value)
                  setSearchInputValue(e.target.value)
                }}
                placeholder={placeholder || 'הקלד מספר מד, כרטיס מים או כתובת'}
                variant="outlined"
                dir="rtl"
                sx={{
                  minWidth: 400,
                  backgroundColor: colors.inputBg,
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-root': {
                    color: colors.inputText,
                    '& fieldset': { borderColor: colors.inputBorder },
                    '&:hover fieldset': { borderColor: colors.inputBorder },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <img src={SearchIcon} alt="Search" style={{ filter: colors.iconFilter, marginLeft: '8px' }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            {proSearch && <ProSearch onClearFilters={onClearFilters} />}
          </Box>
        )}
      </Box>

      {/* 3. Icons Section (Left in RTL flex-end) */}
      <Box sx={{ flex: 1, display: 'flex', gap: 'clamp(8px, 0.8vw, 16px)', alignItems: 'center', justifyContent: 'flex-end' }}>
        <Box className="no-print" sx={{ display: 'flex', gap: 'clamp(8px, 0.8vw, 16px)', alignItems: 'center' }}>
          {orderedIconConfigs.map((config, index) => (
            <MapHeaderIcon
              key={index}
              icon={config.icon}
              alt={config.alt}
              onClick={config.onClick}
            />
          ))}
          {showSearch && moveSearchToIcon && (
            <TextField
              size="small"
              placeholder={placeholder || 'חיפוש...'}
              variant="outlined"
              dir="rtl"
              sx={{
                minWidth: 200,
                backgroundColor: colors.inputBg,
                borderRadius: '8px',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={SearchIcon} alt="Search" style={{ filter: colors.iconFilter, marginLeft: '8px' }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default PageHeaderWithIcons
